import { Facebook, Instagram, MapPin, Phone, Mail, ArrowRight } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import siteContent from '../../data/siteContent.json';

const Footer = () => {
    const { general } = siteContent;
    const location = useLocation();
    const navigate = useNavigate();

    const quickLinks = [
        { name: 'Ana Sayfa', href: '#hero' },
        { name: 'Hakkımızda', href: '#about' },
        { name: 'Hizmetler', href: '#services' },
        { name: 'Süreç', href: '#process' },
        { name: 'Galeri', href: '#gallery' },
        { name: 'İletişim', href: '#contact' }
    ];

    const handleNavClick = (e, href) => {
        e.preventDefault();
        const targetId = href.replace('#', '');

        if (location.pathname === '/') {
            const element = document.getElementById(targetId);
            if (element) {
                const offset = 80;
                const elementPosition = element.getBoundingClientRect().top + window.scrollY;
                window.scrollTo({
                    top: elementPosition - offset,
                    behavior: 'smooth'
                });
            }
        } else {
            navigate('/');
            setTimeout(() => {
                const element = document.getElementById(targetId);
                if (element) {
                    const offset = 80;
                    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
                    window.scrollTo({
                        top: elementPosition - offset,
                        behavior: 'smooth'
                    });
                }
            }, 300);
        }
    };

    const currentYear = new Date().getFullYear();

    return (
        <footer className="bg-[#0a0a0a] text-white pt-14 md:pt-20 pb-10 border-t border-white/5 relative overflow-hidden">
            {/* Background Pattern/Glow Optional */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-gold/5 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 md:gap-12 lg:gap-8 mb-12 md:mb-16">

                    {/* Column 1: Brand (4/12) */}
                    <div className="lg:col-span-4 flex flex-col gap-5">
                        {/* Expanded Brand Block */}
                        <div className="flex items-center gap-3 select-none">
                            <img
                                src="/assets/logos/ERDE-256.png"
                                srcSet="/assets/logos/ERDE-256.png 256w, /assets/logos/ERDE-512.png 512w"
                                sizes="80px"
                                alt="Erde Mobilya"
                                width="80"
                                height="80"
                                loading="lazy"
                                decoding="async"
                                className="h-20 w-20 object-contain brightness-0 invert opacity-90"
                            />
                            <div className="flex flex-col justify-center">
                                <span className="font-heading font-bold text-3xl text-white tracking-widest leading-none mb-1">
                                    ERDE
                                </span>
                                <span className="text-xs md:text-sm text-gray-400 font-light tracking-[0.3em] uppercase">
                                    MOBİLYA & TASARIM
                                </span>
                            </div>
                        </div>

                        <div>
                            <p className="text-lg md:text-xl font-heading font-light text-white mb-2 tracking-wide">
                                Özel Ölçü Mobilya &<br />İç Mekân Tasarımı
                            </p>
                            <p className="text-gray-500 text-xs md:text-sm leading-relaxed max-w-sm">
                                Hayalinizdeki yaşam alanlarını, zanaatkâr el işçiliği ve modern tasarım anlayışıyla gerçeğe dönüştürüyoruz.
                            </p>
                        </div>

                        {/* Social Icons */}
                        <div className="flex items-center gap-3 mt-1">
                            <a href={general.contact.instagram.url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#E4405F] hover:border-[#E4405F] hover:scale-110 hover:shadow-[0_0_15px_rgba(228,64,95,0.3)] transition-all duration-300">
                                <Instagram size={18} strokeWidth={1.5} />
                            </a>
                            <a href={general.contact.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2] hover:scale-110 hover:shadow-[0_0_15px_rgba(24,119,242,0.3)] transition-all duration-300">
                                <Facebook size={18} strokeWidth={1.5} />
                            </a>
                            <a href={general.contact.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#25D366] hover:border-[#25D366] hover:scale-110 hover:shadow-[0_0_15px_rgba(37,211,102,0.3)] transition-all duration-300 group">
                                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* Column 2: Quick Links (3/12) */}
                    <div className="lg:col-span-3 lg:col-start-6">
                        <h3 className="text-white font-heading font-semibold text-lg mb-6 tracking-wide relative inline-block">
                            Hızlı Erişim
                            <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-accent-gold"></span>
                        </h3>
                        <ul className="grid grid-cols-2 gap-y-2 gap-x-4">
                            {quickLinks.map((link) => (
                                <li key={link.name}>
                                    <a
                                        href={link.href}
                                        onClick={(e) => handleNavClick(e, link.href)}
                                        className="text-gray-400 hover:text-accent-gold transition-colors duration-300 text-xs md:text-sm flex items-center gap-2 group cursor-pointer"
                                    >
                                        <ArrowRight size={14} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-accent-gold" />
                                        <span className="-translate-x-4 group-hover:translate-x-0 transition-transform duration-300">{link.name}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Column 3: Contact (4/12) */}
                    <div className="lg:col-span-4 pl-0 lg:pl-8">
                        <h3 className="text-white font-heading font-semibold text-lg mb-6 tracking-wide relative inline-block">
                            İletişim
                            <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-accent-gold"></span>
                        </h3>
                        <div className="flex flex-col gap-4">
                            <a href={`tel:${general.contact.phone.replace(/\s+/g, '')}`} className="flex items-start gap-4 group">
                                <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 flex items-center justify-center text-accent-gold shrink-0 group-hover:bg-accent-gold group-hover:text-white transition-all duration-300 border border-white/10 group-hover:border-accent-gold">
                                    <Phone size={16} strokeWidth={1.5} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1 group-hover:text-accent-gold transition-colors">Telefon</span>
                                    <span className="text-gray-300 group-hover:text-white transition-colors text-sm">{general.contact.phone}</span>
                                </div>
                            </a>

                            <a href={general.contact.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                                <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 flex items-center justify-center text-accent-gold shrink-0 group-hover:bg-accent-gold group-hover:text-white transition-all duration-300 border border-white/10 group-hover:border-accent-gold">
                                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                    </svg>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1 group-hover:text-accent-gold transition-colors">WhatsApp</span>
                                    <span className="text-gray-300 group-hover:text-white transition-colors text-sm">Mesaj Gönder</span>
                                </div>
                            </a>

                            <a href={`mailto:${general.contact.email}`} className="flex items-start gap-4 group">
                                <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 flex items-center justify-center text-accent-gold shrink-0 group-hover:bg-accent-gold group-hover:text-white transition-all duration-300 border border-white/10 group-hover:border-accent-gold">
                                    <Mail size={16} strokeWidth={1.5} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1 group-hover:text-accent-gold transition-colors">E-Posta</span>
                                    <span className="text-gray-300 group-hover:text-white transition-colors text-sm">{general.contact.email}</span>
                                </div>
                            </a>

                            <a href={general.contact.mapLink} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                                <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 flex items-center justify-center text-accent-gold shrink-0 group-hover:bg-accent-gold group-hover:text-white transition-all duration-300 border border-white/10 group-hover:border-accent-gold">
                                    <MapPin size={16} strokeWidth={1.5} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1 group-hover:text-accent-gold transition-colors">Atölye</span>
                                    <span className="text-gray-300 group-hover:text-white transition-colors text-sm max-w-[200px]">{general.contact.address}</span>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="pt-6 md:pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-5 md:gap-6">
                    <div className="flex flex-col items-center md:items-start gap-2">
                        <div className="text-gray-500 text-xs md:text-sm text-center md:text-left">
                            &copy; {currentYear} ERDE Mobilya & Tasarım. Tüm hakları saklıdır.
                        </div>
                        <p className="text-[10px] text-gray-600 font-medium">
                            Bilgileriniz yalnızca sizinle iletişime geçmek amacıyla güvenle saklanır.
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-6 text-xs text-gray-500">
                        <a href="/kvkk" className="hover:text-accent-gold transition-colors">KVKK</a>
                        <a href="/gizlilik-politikasi" className="hover:text-accent-gold transition-colors">Gizlilik Politikası</a>
                        <a href="/cerez-politikasi" className="hover:text-accent-gold transition-colors">Çerez Politikası</a>
                    </div>
                </div>

                {/* Designer Credit */}
                <div className="w-full text-center mt-8 opacity-40 hover:opacity-100 transition-opacity duration-500">
                    <a
                        href="https://instagram.com/ozcanzekii"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] text-gray-500 hover:text-accent-gold transition-colors tracking-widest uppercase font-medium"
                    >
                        Digital Design & UX by Özcan Zeki Ergünen
                    </a>
                </div>

            </div>
        </footer>
    );
};

export default Footer;
